// src/hooks/useCryptoPrice.js

import { useState, useEffect, useCallback } from 'react';
import { getBatchCryptoPrices, clearPriceCache } from './cryptoService';

/**
 * Custom hook for fetching and updating crypto prices
 * Automatically refreshes every 60 seconds
 * 
 * @param {Array} coingeckoIds - Array of CoinGecko IDs to track
 * @param {string} currency - Currency to display prices in (usd or gbp)
 * @param {number} refreshInterval - Refresh interval in milliseconds (default 60000)
 */

export const useCryptoPrice = (coingeckoIds, currency = 'usd', refreshInterval = 60000) => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchPrices = useCallback(async () => {
    if (!coingeckoIds || coingeckoIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const data = await getBatchCryptoPrices(coingeckoIds);
      
      // Extract relevant currency data
      const formattedPrices = {};
      Object.keys(data).forEach(coinId => {
        formattedPrices[coinId] = {
          price: data[coinId][currency],
          change24h: data[coinId][`${currency}_24h_change`],
          marketCap: data[coinId][`${currency}_market_cap`],
          volume: data[coinId][`${currency}_24h_vol`]
        };
      });

      setPrices(formattedPrices);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching crypto prices:', err);
      setError(err.message || 'Failed to fetch prices');
      setLoading(false);
    }
  }, [coingeckoIds, currency]);

  // Initial fetch
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchPrices();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [fetchPrices, refreshInterval]);

  // Manual refresh function
  const refresh = useCallback(() => {
    clearPriceCache();
    fetchPrices();
  }, [fetchPrices]);

  return {
    prices,
    loading,
    error,
    lastUpdate,
    refresh
  };
};

/**
 * Hook for a single crypto price
 */
export const useSingleCryptoPrice = (coingeckoId, currency = 'usd') => {
  const { prices, loading, error, lastUpdate, refresh } = useCryptoPrice(
    coingeckoId ? [coingeckoId] : [],
    currency
  );

  return {
    price: prices[coingeckoId],
    loading,
    error,
    lastUpdate,
    refresh
  };
};

export default useCryptoPrice;
